import React, { useEffect, useMemo, useState } from "react";
import { useAddManagerToGroupMutation } from "../../redux/admin/groupApi";
import { useGetAssignableUsersQuery } from "../../redux/admin/adminApi";
import { useGetUserProfileQuery } from "../../redux/admin/userApi";
import { toast } from "react-hot-toast";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { X, Search, Mail, ShieldCheck, UserPlus, Check } from "lucide-react";

const getRolesToFetch = (loggedInUserRole) => {
  const role = loggedInUserRole?.toLowerCase();
  switch (role) {
    case "admin":
      return ["manager", "sub_admin", "group_admin", "user"];
    case "manager":
      return ["sub_admin", "group_admin", "user"];
    case "sub_admin":
      return ["group_admin", "user"];
    case "group_admin":
      return ["user"];
    default:
      return [];
  }
};

const MANAGER_ROLES = [
  { value: "manager", label: "manager", desc: "Full management access" },
  { value: "operator", label: "Operator", desc: "Can operate devices" },
  { value: "viewer", label: "Viewer", desc: "View only access" },
];

const roleChipColor = (role) => {
  switch ((role || "").toLowerCase()) {
    case "admin":
      return "error";
    case "manager":
      return "secondary";
    case "sub_admin":
      return "info";
    case "group_admin":
      return "warning";
    default:
      return "default";
  }
};

const prettyRole = (role) => {
  const map = {
    admin: "Admin",
    Manager: "manager",
    manager: "manager",
    sub_admin: "Admin",
    group_admin: "Dealer",
    user: "User",
  };
  return map[role] || role;
};

const AddManagerModal = ({ groupId, isOpen, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRole, setSelectedRole] = useState("manager");

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedUserId(null);
      setSelectedRole("manager");
    }
  }, [isOpen]);

  const { data: userProfile, isLoading: isProfileLoading } =
    useGetUserProfileQuery(undefined, { skip: !isOpen });

  const loggedInUserRole = userProfile?.role || "user";
  const rolesToFetch = useMemo(
    () => getRolesToFetch(loggedInUserRole),
    [loggedInUserRole]
  );

  const canAddManagers = rolesToFetch.length > 0;

  const { data: assignableUsers, isLoading: isUsersLoading } =
    useGetAssignableUsersQuery(rolesToFetch, {
      skip: !isOpen || isProfileLoading || rolesToFetch.length === 0,
    });

  const [addManager, { isLoading: isAdding }] = useAddManagerToGroupMutation();

  const users = useMemo(
    () => (Array.isArray(assignableUsers) ? assignableUsers : []),
    [assignableUsers]
  );

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const isLoading = isProfileLoading || isUsersLoading;

  const handleSubmit = async () => {
    if (!groupId) return toast.error("Group not found");
    if (!selectedUserId) return toast.error("Please select a user");

    try {
      await addManager({
        groupId,
        userId: selectedUserId,
        role: selectedRole,
      }).unwrap();

      toast.success("Manager added successfully!");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.msg || "Failed to add manager");
    }
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 2,
          background: alpha(theme.palette.secondary.main, 0.08),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.2,
                  display: "grid",
                  placeItems: "center",
                  background: theme.palette.secondary.main,
                  color: "white",
                }}
              >
                <ShieldCheck size={18} />
              </Box>
              <Typography variant="h6" fontWeight={900}>
                Add Manager
              </Typography>
              {isLoading && (
                <Chip
                  size="small"
                  label="Loading…"
                  variant="outlined"
                  sx={{ borderRadius: 2, fontWeight: 800 }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Assign a user to manage this group (role based access).
            </Typography>
          </Stack>

          <Tooltip title="Close">
            <IconButton onClick={onClose} sx={{ borderRadius: 2 }}>
              <X size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2, background: alpha(theme.palette.background.default, 0.35) }}>
        {!canAddManagers ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Your role ({prettyRole(loggedInUserRole)}) doesn’t allow adding managers.
            </Alert>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {/* Role selection */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              }}
            >
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Manager Role
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {MANAGER_ROLES.map((r) => {
                  const active = selectedRole === r.value;
                  return (
                    <Paper
                      key={r.value}
                      elevation={0}
                      onClick={() => setSelectedRole(r.value)}
                      sx={{
                        flex: 1,
                        p: 1.4,
                        borderRadius: 3,
                        cursor: "pointer",
                        border: `1px solid ${
                          active
                            ? alpha(theme.palette.secondary.main, 0.45)
                            : alpha(theme.palette.divider, 0.9)
                        }`,
                        background: active
                          ? alpha(theme.palette.secondary.main, 0.08)
                          : theme.palette.background.paper,
                        transition: "0.12s ease",
                        "&:hover": {
                          background: alpha(theme.palette.secondary.main, 0.06),
                        },
                      }}
                    >
                      <Stack spacing={0.4}>
                        <Typography fontWeight={900}>{r.label}</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {r.desc}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>

            {/* Search */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              }}
            >
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name or email…"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                Your role: <b>{prettyRole(loggedInUserRole)}</b> • Can assign:{" "}
                {rolesToFetch.map(prettyRole).join(", ")}
              </Typography>
            </Paper>

            {/* Users list */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                overflow: "hidden",
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1.2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Box key={i} sx={{ p: 1.2 }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Skeleton variant="rounded" width={42} height={42} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="45%" />
                            <Skeleton width="65%" />
                          </Box>
                          <Skeleton width={36} height={36} variant="rounded" />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : filteredUsers.length > 0 ? (
                <Box sx={{ maxHeight: fullScreen ? "calc(100vh - 300px)" : 360, overflow: "auto" }}>
                  {filteredUsers.map((u, idx) => {
                    const selected = selectedUserId === u._id;
                    return (
                      <Box key={u._id}>
                        <Box
                          onClick={() => setSelectedUserId(u._id)}
                          sx={{
                            px: 1.5,
                            py: 1.2,
                            cursor: "pointer",
                            background: selected
                              ? alpha(theme.palette.secondary.main, 0.1)
                              : theme.palette.background.paper,
                            "&:hover": { background: alpha(theme.palette.secondary.main, 0.06) },
                            transition: "0.12s ease",
                          }}
                        >
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            {/* avatar */}
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2.3,
                                display: "grid",
                                placeItems: "center",
                                background: alpha(theme.palette.secondary.main, 0.12),
                                color: theme.palette.secondary.main,
                                fontWeight: 900,
                                flexShrink: 0,
                              }}
                            >
                              {(u.username?.[0] || "?").toUpperCase()}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography fontWeight={900} noWrap>
                                  {u.username || "Unknown"}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={prettyRole(u.role)}
                                  color={roleChipColor(u.role)}
                                  variant="outlined"
                                  sx={{ borderRadius: 2, fontWeight: 800 }}
                                />
                              </Stack>

                              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                                <Mail size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                {u.email || "No email"}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 2,
                                display: "grid",
                                placeItems: "center",
                                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                background: selected ? theme.palette.secondary.main : "transparent",
                                color: selected ? "white" : "transparent",
                              }}
                            >
                              <Check size={18} />
                            </Box>
                          </Stack>
                        </Box>
                        {idx !== filteredUsers.length - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography fontWeight={900}>No users found</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Try a different search.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          background: theme.palette.background.paper,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}
          alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {selectedUserId
              ? `1 user selected • ${MANAGER_ROLES.find((r) => r.value === selectedRole)?.label}`
              : "Select a user to continue"}
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={isAdding}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isAdding || !selectedUserId || !canAddManagers}
              startIcon={
                isAdding ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  <UserPlus size={18} />
                )
              }
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                backgroundColor: theme.palette.secondary.main,
                "&:hover": { backgroundColor: theme.palette.secondary.dark },
              }}
            >
              {isAdding ? "Adding…" : "Add Manager"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddManagerModal;
